import {
  actionColumn,
  booleanFilter,
  Button,
  collapseColumn,
  column,
  ContentHeader,
  Css,
  dateColumn,
  emptyCell,
  FilterDefs,
  Filters,
  GridDataRow,
  GridSortConfig,
  GridTable,
  GridTableApi,
  GridTableLayout,
  HeaderAction,
  multiFilter,
  PageHeaderLayout,
  Palette,
  RightPaneLayout,
  RowStyles,
  ScrollableContent,
  selectColumn,
  simpleHeader,
  Tag,
  toggleFilter,
  useComputed,
  useGridTableApi,
  useGridTableLayoutState,
  useGroupBy,
  useModal,
  usePersistedFilter,
  useRightPane,
  useSnackbar,
  useTestIds,
} from "@homebound/beam";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { dateCell, FileUploadProgressAlert, SearchBox, UppyUploader } from "src/components";
import { Icon } from "src/components/Icon";
import { AssetUppyFile, useUppyUploader } from "src/components/uploader/UppyUploader";
import { UploaderProvider } from "src/contexts/UploaderContext";
import {
  DocumentDetailDocumentTypeFragment,
  NamedDevelopmentCohortsProjectsFragment,
} from "src/fragments/circularFragments.generated";
import {
  DocumentCategory,
  DocumentCategoryDetail,
  DocumentsFilter,
  DocumentType,
  Project,
  SaveDocumentInput,
} from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import { PageHeader } from "src/routes/layout/PageHeader";
import { TableActions } from "src/routes/layout/TableActions";
import { DocumentsActionsMenu, useDocumentsActionMenuItems } from "src/routes/projects/documents/DocumentsActionsMenu";
import {
  DocumentsPageDocumentDetailFragment,
  DocumentsPageDocumentParentFragment,
  DocumentsPageProjectDetailFragment,
  useDocumentsPageMetadataQuery,
  useDocumentsPageQuery,
  useSaveDocumentsMutation,
  useToggleDocumentAsFavoritedMutation,
} from "src/routes/projects/documents/DocumentsPage.generated";
import { UpdateGDriveModal } from "src/routes/projects/documents/UpdateGDriveModal";
import { DocumentsParams } from "src/RouteUrls";
import {
  formatNumberToString,
  isDefined,
  isEmpty,
  nonEmpty,
  partition,
  pushInNextLoop,
  safeEntries,
  sortBy,
} from "src/utils";
import { getGoogleDriveLink } from "src/utils/googleDrive";
import { isProjectType } from "src/utils/projects";
import { queryResult } from "src/utils/queryResult";
import { zIndexes } from "src/utils/styles/zIndexes";
import { StringParam, useQueryParams } from "use-query-params";
import { DocumentEditor } from "./DocumentEditor";

type HeaderRow = { kind: "header" };
type DocumentRow = { kind: "document"; data: DocumentsPageDocumentDetailFragment };
type GroupedByRow = { kind: "group"; data: { category?: string; project?: string; childCount?: number } };
type Row = HeaderRow | DocumentRow | GroupedByRow;

export const DOCUMENT_LIMIT = 500;

type DocumentsPageProps = {
  readyPlanId?: string;
  versionId?: string;
  hideHeader?: boolean;
  stickyHeader?: boolean;
  /** Opt-in Beam GridTableLayout + document-scroll right pane. Used by Plan Package V2; other hosts stay on the legacy chrome. */
  useGridTableLayout?: boolean;
  /** Render the GridTable path as a standalone Beam page instead of embedded tab content. */
  usePageHeaderLayout?: boolean;
};

export enum DocumentsPageMode {
  Development = "development",
  ReadyPlan = "readyPlan",
  Project = "project",
  Hierarchy = "hierarchy",
}

export function DocumentsPage(props: DocumentsPageProps) {
  const { projectId, developmentId } = useRouteParams<{ projectId?: string; developmentId?: string }>();
  const { readyPlanId, versionId, hideHeader, stickyHeader = true, useGridTableLayout, usePageHeaderLayout } = props;
  const parentId = readyPlanId ?? developmentId ?? projectId!;
  const metaDataQuery = useDocumentsPageMetadataQuery({
    variables: { parentId, skipDevelopment: !isDefined(developmentId), skipProject: !isDefined(projectId) },
  });
  const mode = isDefined(readyPlanId)
    ? DocumentsPageMode.ReadyPlan
    : isDefined(developmentId)
      ? DocumentsPageMode.Development
      : DocumentsPageMode.Project;

  return queryResult(metaDataQuery, {
    data: ({ documentTypes, documentCategories, development, project }) => (
      <DocumentsDataView
        mode={project && project.childrenRecursive.nonEmpty ? DocumentsPageMode.Hierarchy : mode}
        parentId={parentId}
        versionId={versionId}
        hideHeader={hideHeader}
        stickyHeader={stickyHeader}
        useGridTableLayout={useGridTableLayout}
        usePageHeaderLayout={usePageHeaderLayout}
        documentTypes={documentTypes.sortByKey("name")}
        documentCategories={documentCategories.sortByKey("name")}
        development={development}
        project={project}
      />
    ),
  });
}

/** Project route entry point after migrating Documents to Beam page chrome. */
export function ProjectDocumentsPage() {
  return <DocumentsPage useGridTableLayout usePageHeaderLayout />;
}

/** Development route entry point after migrating Documents to Beam page chrome. */
export function DevelopmentDocumentsPage() {
  return <DocumentsPage useGridTableLayout usePageHeaderLayout />;
}

type GroupBy = "category" | "project" | "none";

type DocumentsDataViewProps = {
  mode: DocumentsPageMode;
  parentId: string;
  versionId?: string;
  hideHeader?: boolean;
  stickyHeader: boolean;
  useGridTableLayout?: boolean;
  usePageHeaderLayout?: boolean;
  documentTypes: DocumentDetailDocumentTypeFragment[];
  documentCategories: DocumentCategoryDetail[];
  development?: NamedDevelopmentCohortsProjectsFragment;
  project?: DocumentsPageProjectDetailFragment;
};

function DocumentsDataView(props: DocumentsDataViewProps) {
  const { useGridTableLayout, ...rest } = props;
  // Separate components so filter/search/groupBy hooks stay unconditional within each layout path.
  if (useGridTableLayout) {
    return (
      <UploaderProvider>
        <GridDocumentsDataView {...rest} />
      </UploaderProvider>
    );
  }
  return <LegacyDocumentsDataView {...rest} />;
}

function createDocumentsFilterDefs(
  mode: DocumentsPageMode,
  documentCategories: DocumentCategoryDetail[],
): FilterDefs<DocumentsFilter> {
  const deSelectedCategories = [
    DocumentCategory.Accounting,
    DocumentCategory.Contractual,
    DocumentCategory.Procurement,
  ] as DocumentCategory[];

  const category = multiFilter({
    label: "Category",
    options: sortBy(documentCategories, (category) => category.name),
    defaultValue: documentCategories
      .filter((category) => !deSelectedCategories.includes(category.code))
      .map((category) => category.code),
    getOptionValue: (o) => o.code,
    getOptionLabel: (o) => o.name,
  });

  const homeownerVisible = booleanFilter({
    label: "Visibility",
    options: [
      [undefined, "All"],
      [true, "Homeowner"],
      [false, "Internal Only"],
    ],
  });

  const label = mode === DocumentsPageMode.ReadyPlan ? "Show Removed" : "Show Archived";
  const includeDeleted = toggleFilter({ label });

  return { category, ...(mode === DocumentsPageMode.ReadyPlan ? {} : { homeownerVisible }), includeDeleted };
}

function groupByOptionsForMode(mode: DocumentsPageMode): Record<string, string> {
  if (mode === DocumentsPageMode.Development || mode === DocumentsPageMode.Hierarchy) {
    return { category: "Category", project: "Project", none: "None" };
  }
  return { category: "Category", none: "None" };
}

type SharedDocumentsLayoutProps = Omit<DocumentsDataViewProps, "useGridTableLayout">;

/** Plan Package V2 path: GridTableLayout chrome + document-scroll useRightPane. */
function GridDocumentsDataView(props: SharedDocumentsLayoutProps) {
  const { mode, documentTypes, documentCategories, development, parentId, versionId, project, usePageHeaderLayout } =
    props;
  const tid = useTestIds({}, "documentsPage");
  const { openModal } = useModal();
  const parentProject = mode === DocumentsPageMode.Hierarchy ? project : undefined;

  const filterDefs = useMemo(
    () => createDocumentsFilterDefs(mode, documentCategories),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [documentCategories, mode, parentId, versionId],
  );

  const layoutState = useGridTableLayoutState<DocumentsFilter>({
    persistedFilter: { storageKey: `${parentId}_documentsFilter`, filterDefs },
    search: "client",
    groupBy: groupByOptionsForMode(mode),
  });
  const groupByValue = (layoutState.groupBy?.value ?? "category") as GroupBy;

  const { triggerNotice } = useSnackbar();
  const projects = isDefined(project)
    ? [project, ...project.childrenRecursive]
    : (development?.cohorts?.flatMap((c) => c.projects) ?? []);
  const parentIds = [development?.id, ...projects.map((p) => p.id)].compact();

  const query = useDocumentsPageQuery({
    variables: {
      filter: {
        ...layoutState.filter,
        ...(mode === DocumentsPageMode.ReadyPlan ? { readyPlanId: parentId, versionId } : { parentIds }),
      },
      page: { limit: DOCUMENT_LIMIT },
    },
    skip: isEmpty(parentIds) && !isDefined(parentId),
  });

  useDocumentsTruncationNotice(query.data, triggerNotice);
  const { documentId, handleOnFinish, setAllowDocRedirect } = useDocumentsUploadAndPane({
    mode,
    parentId,
    versionId,
    documentTypes,
    groupBy: groupByValue,
  });

  const tableApi = useGridTableApi<Row>();
  const selectedDocuments: DocumentsPageDocumentDetailFragment[] = useComputed(
    () => tableApi.getSelectedRows("document").map((row) => row.data),
    [tableApi],
  );

  const actionMenuItems = useDocumentsActionMenuItems({
    mode,
    versionId,
    groupBy: groupByValue,
    selectedDocuments,
    documentTypes,
    tableRefetch: query.refetch,
    tableApi,
    development,
    parentProject,
    hideArchiveActions: true,
  });

  const uploadInputRef = useRef<HTMLInputElement>(null);
  const uppy = useUppyUploader({ onFinish: handleOnFinish, onComplete: () => setAllowDocRedirect(true) });

  const actions: HeaderAction[] = useMemo(
    () => [
      {
        kind: "menu",
        items: actionMenuItems,
        trigger: { icon: "verticalDots", variant: "outline" },
        tooltip: "Actions",
      },
    ],
    [actionMenuItems],
  );

  const columns = useDocumentsColumns({ mode, documentCategories, projects, groupBy: groupByValue });
  const documentsUrl = useDocumentsUrl();
  const rowStyles: RowStyles<Row> = useMemo(
    () => ({
      header: {},
      document: { rowLink: ({ data }) => documentsUrl({ versionId, groupBy: groupByValue, documentId: data.id }) },
    }),
    [documentsUrl, versionId, groupByValue],
  );

  return queryResult(query, {
    data: ({ documents }) => {
      const content = (
        <>
          <GridTableLayout
            hideEditColumns
            layoutState={layoutState}
            withRightPane={450}
            emptyFallback="No documents found"
            actions={actions}
            tableProps={{
              as: "virtual",
              api: tableApi,
              columns,
              rows: createRows(documents.entities, groupByValue, parentId),
              rowStyles,
              sorting: { on: "client", primary: ["favorite", "DESC"] },
              style: { rowHeight: "fixed", grouped: groupByValue !== "none", allWhite: true },
              activeRowId: documentId ? `document_${documentId}` : undefined,
            }}
          />
          <div css={Css.fixed.bottom2.w50.add("zIndex", zIndexes.modal).$} data-testid="documentsPage_fileUploader">
            <FileUploadProgressAlert />
          </div>
        </>
      );

      return (
        <div {...tid}>
          <input
            ref={uploadInputRef}
            type="file"
            multiple
            // Preserve class name for test utility compatibility (getUppyDragDropInput)
            className="uppy-DragDrop-input"
            css={Css.dn.$}
            data-testid="documentsPage_uploadInput"
            onChange={(e) => {
              const files = e.target.files;
              if (!files) return;
              Array.from(files).forEach((file) => {
                try {
                  uppy.addFile({ name: file.name, size: file.size, type: file.type, data: file });
                } catch (err) {
                  console.warn(err);
                }
              });
              e.target.value = "";
            }}
          />
          {usePageHeaderLayout ? (
            <PageHeaderLayout
              pageHeader={{
                title: "Documents",
                documentTitleSuffix: project ? `${project.name}` : "",
                actions: [
                  ...(project
                    ? [
                        {
                          label: "Edit GDrive Link",
                          icon: "pencil" as const,
                          variant: "secondary" as const,
                          onClick: () =>
                            openModal({
                              content: (
                                <UpdateGDriveModal
                                  projectId={parentId}
                                  googleDriveLink={getGoogleDriveLink(project.googleDriveFolderId || "")}
                                  refetch={query.refetch}
                                />
                              ),
                            }),
                        },
                      ]
                    : []),
                  {
                    label: "Upload",
                    icon: "upload",
                    variant: "secondary",
                    onClick: () => uploadInputRef.current?.click(),
                  },
                ],
              }}
            >
              {content}
            </PageHeaderLayout>
          ) : (
            <>
              <ContentHeader
                title="Documents"
                actions={[
                  {
                    label: "Upload",
                    icon: "upload",
                    variant: "secondary",
                    onClick: () => uploadInputRef.current?.click(),
                  },
                ]}
                xss={Css.px3.pt3.$}
              />
              {content}
            </>
          )}
        </div>
      );
    },
  });
}

/** Project / Development / legacy Plan Package path: TableActions + RightPaneLayout. */
function LegacyDocumentsDataView(props: SharedDocumentsLayoutProps) {
  const {
    mode,
    documentTypes,
    documentCategories,
    development,
    parentId,
    versionId,
    hideHeader,
    stickyHeader,
    project,
  } = props;
  const tid = useTestIds({}, "documentsPage");
  const parentProject = mode === DocumentsPageMode.Hierarchy ? project : undefined;

  const filterDefs = useMemo(
    () => createDocumentsFilterDefs(mode, documentCategories),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [documentCategories, mode, parentId, versionId],
  );
  const groupBy = useGroupBy<GroupBy>(groupByOptionsForMode(mode));
  const { setFilter, filter } = usePersistedFilter<DocumentsFilter>({
    storageKey: `${parentId}_documentsFilter`,
    filterDefs,
  });
  const { openModal } = useModal();
  const { triggerNotice } = useSnackbar();

  const projects = isDefined(project)
    ? [project, ...project.childrenRecursive]
    : (development?.cohorts?.flatMap((c) => c.projects) ?? []);

  const parentIds = [development?.id, ...projects.map((p) => p.id)].compact();

  const query = useDocumentsPageQuery({
    variables: {
      filter: {
        ...filter,
        ...(mode === DocumentsPageMode.ReadyPlan ? { readyPlanId: parentId, versionId } : { parentIds }),
      },
      page: { limit: DOCUMENT_LIMIT },
    },
    skip: isEmpty(parentIds) && !isDefined(parentId),
  });

  useDocumentsTruncationNotice(query.data, triggerNotice);

  const [searchFilter, setSearchFilter] = useState<string | undefined>();
  const { documentId, handleOnFinish, setAllowDocRedirect } = useDocumentsUploadAndPane({
    mode,
    parentId,
    versionId,
    documentTypes,
    groupBy: groupBy.value,
  });

  const tableApi = useGridTableApi<Row>();

  const selectedDocuments: DocumentsPageDocumentDetailFragment[] = useComputed(
    () => tableApi.getSelectedRows("document").map((row) => row.data),
    [tableApi],
  );

  return queryResult(query, {
    data: ({ documents }) => {
      return (
        <div {...tid}>
          <UploaderProvider>
            {!hideHeader && (
              <PageHeader
                xss={Css.bgWhite.$}
                title="Documents"
                right={
                  isDefined(project) && (
                    <>
                      <Button
                        label="Edit GDrive Link"
                        variant="secondary"
                        icon="pencil"
                        onClick={() =>
                          openModal({
                            content: (
                              <UpdateGDriveModal
                                projectId={parentId}
                                googleDriveLink={getGoogleDriveLink(project.googleDriveFolderId || "")}
                                refetch={query.refetch}
                              />
                            ),
                          })
                        }
                      />
                      {project.googleDriveFolderId && (
                        <Button label="Google Drive" onClick={getGoogleDriveLink(project.googleDriveFolderId)} />
                      )}
                    </>
                  )
                }
              />
            )}
            <TableActions>
              <Filters groupBy={groupBy} filter={filter} onChange={setFilter} filterDefs={filterDefs} />
              <div css={Css.df.aic.gap2.$}>
                <SearchBox onSearch={setSearchFilter} />
                <DocumentsActionsMenu
                  mode={mode}
                  versionId={versionId}
                  selectedDocuments={selectedDocuments}
                  documentTypes={documentTypes}
                  tableRefetch={query.refetch}
                  tableApi={tableApi}
                  development={development}
                  parentProject={parentProject}
                />
                <UppyUploader onFinish={handleOnFinish} onComplete={() => setAllowDocRedirect(true)} />
              </div>
            </TableActions>
            <ScrollableContent omitBottomPadding>
              <RightPaneLayout>
                <>
                  <DocumentsTable
                    mode={mode}
                    documents={documents.entities}
                    parentId={parentId}
                    versionId={versionId}
                    stickyHeader={stickyHeader}
                    projects={projects}
                    searchFilter={searchFilter}
                    selectedDocumentId={documentId}
                    tableApi={tableApi}
                    documentCategories={documentCategories}
                    groupBy={groupBy.value}
                  />
                  <div
                    css={Css.fixed.bottom2.w50.add("zIndex", zIndexes.modal).$}
                    data-testid="documentsPage_fileUploader"
                  >
                    <FileUploadProgressAlert />
                  </div>
                </>
              </RightPaneLayout>
            </ScrollableContent>
          </UploaderProvider>
        </div>
      );
    },
  });
}

function useDocumentsTruncationNotice(
  data: ReturnType<typeof useDocumentsPageQuery>["data"],
  triggerNotice: ReturnType<typeof useSnackbar>["triggerNotice"],
) {
  // Key on the query result's value, not the reference Apollo re-creates on unrelated cache
  // writes, so the truncation warning only re-fires when the fetched documents actually change.
  const documentsKey = useMemo(() => JSON.stringify(data), [data]);
  useEffect(() => {
    if (data?.documents.pageInfo.hasNextPage) {
      triggerNotice({
        icon: "warning",
        message: `This list has been truncated to ${formatNumberToString(DOCUMENT_LIMIT, true)} documents, please add more filters.`,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentsKey, triggerNotice]);
}

function useDocumentsUploadAndPane(opts: {
  mode: DocumentsPageMode;
  parentId: string;
  versionId?: string;
  documentTypes: DocumentDetailDocumentTypeFragment[];
  groupBy: GroupBy;
}) {
  const { mode, parentId, versionId, documentTypes, groupBy } = opts;
  const [allowDocRedirect, setAllowDocRedirect] = useState(true);
  const [{ documentId }] = useQueryParams({ documentId: StringParam });
  const [saveDocuments] = useSaveDocumentsMutation();
  const navigate = useNavigate();
  const documentsUrl = useDocumentsUrl();
  const { openRightPane, closeRightPane } = useRightPane();
  const [newVersionId, setNewVersionId] = useState<string | undefined>();

  useEffect(() => {
    // After uploading a new document, we go through a loop where `documentId` is updated but `versionId` is not. This
    // then fires a query for a document in a version it doesn't for, which results in a backend error. So when we
    // transition to a new draft, we store the version we are transitioning to and wait until it's populated here to
    // display the document editor pane
    documentId && (mode !== DocumentsPageMode.ReadyPlan || newVersionId === undefined || newVersionId === versionId)
      ? openRightPane({
          content: (
            <DocumentEditor
              documentId={documentId}
              groupBy={groupBy}
              documentTypes={documentTypes}
              versionId={versionId}
            />
          ),
        })
      : closeRightPane();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId, versionId, newVersionId, groupBy]);

  useEffect(() => {
    if (newVersionId === versionId) setNewVersionId(undefined);
  }, [versionId, newVersionId, setNewVersionId]);

  async function handleOnFinish(file: AssetUppyFile) {
    // This is to prevent multiple file uploads to redirect one after each other
    if (allowDocRedirect) {
      setAllowDocRedirect(false);
    }
    const saveDocumentsInput: [SaveDocumentInput] = [
      {
        documentType: inferDocumentType(file.name),
        name: file.name,
        sizeInBytes: file.size,
        parentId: parentId,
        asset: {
          contentType: file.type,
          fileName: file.name,
          s3Key: file.meta.s3Key as string,
          sizeInBytes: file.size,
        },
      },
    ];
    const { data } = await saveDocuments({
      variables: { input: saveDocumentsInput },
      refetchQueries: ["DocumentsPage"],
    });

    const savedDoc = data?.saveDocuments.documents[0];
    if (savedDoc && allowDocRedirect) {
      const nextVersionId =
        savedDoc.parent.__typename === "PlanPackage" ? savedDoc.parent.aggregateDraft?.id : undefined;
      setNewVersionId(nextVersionId);
      const url = documentsUrl({ versionId: nextVersionId, groupBy, documentId: savedDoc.id });
      navigate(url);
    }
  }

  return { documentId, handleOnFinish, allowDocRedirect, setAllowDocRedirect };
}

type DocumentsTableProps = {
  mode: DocumentsPageMode;
  documents: DocumentsPageDocumentDetailFragment[];
  parentId: string;
  versionId?: string;
  stickyHeader: boolean;
  projects: Pick<Project, "id" | "name">[];
  searchFilter?: string;
  selectedDocumentId: string | undefined | null;
  tableApi: GridTableApi<Row>;
  documentCategories: DocumentCategoryDetail[];
  groupBy: GroupBy;
};

function DocumentsTable(props: DocumentsTableProps) {
  const {
    mode,
    documents,
    projects,
    parentId,
    versionId,
    stickyHeader,
    searchFilter = "",
    selectedDocumentId,
    tableApi,
    documentCategories,
    groupBy,
  } = props;

  const documentsUrl = useDocumentsUrl();

  // GroupBy is not store in the filters so we need set it again
  const rowStyles: RowStyles<Row> = useMemo(
    () => ({
      header: {},
      document: { rowLink: ({ data }) => documentsUrl({ versionId, groupBy, documentId: data.id }) },
    }),
    [documentsUrl, versionId, groupBy],
  );

  const rows = useMemo(
    () => (documents ? createRows(documents, groupBy, parentId) : []),
    [documents, groupBy, parentId],
  );
  const columns = useDocumentsColumns({ mode, documentCategories, projects, groupBy });

  // Find is setting the primary column to favoriteColumn
  const sortingConfig: GridSortConfig = { on: "client", primary: ["favorite", "DESC"] };

  return (
    <GridTable
      as="virtual"
      columns={columns}
      rows={rows}
      rowStyles={rowStyles}
      sorting={sortingConfig}
      stickyHeader={stickyHeader}
      filter={searchFilter}
      style={{ rowHeight: "fixed", grouped: groupBy !== "none", bordered: true, allWhite: true }}
      activeRowId={selectedDocumentId ? `document_${selectedDocumentId}` : undefined}
      api={tableApi}
      fallbackMessage="No documents found"
    />
  );
}

function useDocumentsColumns(opts: {
  mode: DocumentsPageMode;
  documentCategories: DocumentCategoryDetail[];
  projects: Pick<Project, "id" | "name">[];
  groupBy: GroupBy;
}) {
  const { mode, documentCategories, projects, groupBy } = opts;
  const [toggleDocumentAsFavoritedMutation] = useToggleDocumentAsFavoritedMutation();

  return useMemo(() => {
    const categoryColumn = column<Row>({
      header: "Category",
      document: (document) => documentCategories?.find((d) => d.code === document.category)?.name ?? " ",
      mw: "140px",
      group: (data) => {
        const { category, childCount } = data;
        return {
          content: () =>
            category ? (
              <>
                <div css={Css.mr1.$}>{documentCategories?.find((d) => d.code === category)?.name ?? "No Category"}</div>
                {childCount && childCount > 0 && <Tag text={childCount.toString()} />}
              </>
            ) : (
              ""
            ),
          sortValue: data.category,
          colspan: 7,
        };
      },
    });
    const favoriteColumn = actionColumn<Row>({
      id: "favorite",
      header: emptyCell,
      document: (document) => ({
        content: (
          <Icon
            icon={document.isFavorited ? "starFilled" : "star"}
            color={document.isFavorited ? Palette.Yellow500 : Palette.Gray900}
            onClick={() => toggleDocumentAsFavoritedMutation({ variables: { input: { documentId: document.id } } })}
          />
        ),
        sortValue: document.isFavorited,
      }),
      w: "45px",
      group: emptyCell,
      clientSideSort: false,
    });
    const nameColumn = column<Row>({
      header: "Name",
      document: (document) => ({ content: document.displayName, value: document.displayName.toLowerCase() }),
      mw: "300px",
      group: emptyCell,
    });
    const typeColumn = column<Row>({
      header: "Type",
      document: ({ documentType: { name, code } }) =>
        code === DocumentType.General ? { content: () => <Icon icon="error" />, value: undefined } : name,
      mw: "120px",
      group: emptyCell,
    });
    const dateIssuanceColumn = dateColumn<Row>({
      header: "Issuance Date",
      document: (document) => dateCell(document.issuanceDate),
      w: "110px",
      group: emptyCell,
    });
    const hoVisibleColumn = column<Row>({
      header: "H/O Visibile",
      document: ({ homeownerVisible }) => (homeownerVisible ? "Yes" : "No"),
      w: "100px",
      group: emptyCell,
    });
    const archiveColumn = column<Row>({
      header: "  ",
      document: ({ isDeleted }) => (isDeleted ? <Icon icon={"archive"} /> : " "),
      w: "45px",
      clientSideSort: false,
      group: emptyCell,
    });
    const projectColumn = column<Row>({
      header: "Project",
      document: ({ parent }) => (isProjectType(parent) ? projects.find(({ id }) => id === parent?.id)?.name : "--"),
      mw: "160px",
      group: (data) => {
        const { project, childCount } = data;
        return {
          content: () =>
            project ? (
              <>
                <div css={Css.mr1.$}>{project}</div>
                {childCount && childCount > 0 && <Tag text={childCount.toString()} />}
              </>
            ) : (
              <></>
            ),
          sortValue: data.project,
          colspan: 7,
        };
      },
    });

    return [
      collapseColumn<Row>(),
      selectColumn<Row>(),
      favoriteColumn,
      ...(groupBy === "category" ? [categoryColumn] : []),
      ...(groupBy === "project" ? [projectColumn] : []),
      nameColumn,
      typeColumn,
      ...(groupBy !== "category" ? [categoryColumn] : []),
      ...(mode === DocumentsPageMode.ReadyPlan ? [] : [dateIssuanceColumn, hoVisibleColumn]),
      ...((mode === DocumentsPageMode.Development || mode === DocumentsPageMode.Hierarchy) && groupBy !== "project"
        ? [projectColumn]
        : []),
      archiveColumn,
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentCategories, groupBy, mode, projects]);
}

function createRows(documents: DocumentsPageDocumentDetailFragment[], groupBy: GroupBy, parentId: string) {
  const createDocumentRow = (doc: DocumentsPageDocumentDetailFragment) => ({
    kind: "document" as const,
    id: doc.id,
    data: doc,
  });
  const kind = "group" as const;
  if (groupBy === "category") {
    const [withCategory, noCategory] = partition(documents, (d) => !!d.category);
    const groups = withCategory.groupBy((doc) => doc.category!);
    const rows = safeEntries(groups).map(([groupName, docList]) => ({
      kind,
      id: groupName,
      data: { category: groupName, childCount: docList.length },
      children: docList.map(createDocumentRow),
    }));
    return [
      simpleHeader,
      ...rows,
      ...(nonEmpty(noCategory)
        ? [
            {
              kind,
              id: "No Category",
              data: { category: "No Category", childCount: noCategory.length },
              children: noCategory.map(createDocumentRow),
              fixedSort: "last" as const,
            },
          ]
        : []),
    ];
  } else if (groupBy === "project") {
    const [parentLevelDocuments, projectDocuments] = documents.partition((d) => d.parent.id === parentId);
    const parentLabel = parentLevelDocuments.first?.parent.__typename ?? "Development";
    return [
      simpleHeader,
      ...(projectDocuments.nonEmpty
        ? [
            {
              kind,
              id: "Project Level",
              data: { project: "Project Level", childCount: projectDocuments.length },
              children: projectDocuments.map(createDocumentRow),
            },
          ]
        : []),
      ...(parentLevelDocuments.nonEmpty
        ? [
            {
              kind,
              id: `${parentLabel} Level`,
              data: { project: `${parentLabel} Level`, childCount: parentLevelDocuments.length },
              children: parentLevelDocuments.map(createDocumentRow),
              fixedSort: "last" as const,
            },
          ]
        : []),
    ] satisfies GridDataRow<Row>[];
  } else {
    return [simpleHeader, ...documents.map(createDocumentRow)];
  }
}

// Maps filename patterns to a best-guess DocumentType at upload time (APP-1580). Users can still
// override the guess via the Type field in the DocumentEditor.
const documentTypeFileNamePatterns: [RegExp, DocumentType][] = [
  [/finish[\s_-]*schedule/i, DocumentType.FinishSchedule],
];

export function inferDocumentType(fileName: string): DocumentType {
  return documentTypeFileNamePatterns.find(([regex]) => regex.test(fileName))?.[1] ?? DocumentType.General;
}

/**
 * Urls this page points at: row links and redirects.
 *
 * Derived from the current location rather than `createDocumentsUrl`, so links stay on whichever host page is
 * rendering us: project, development, or the legacy/V2 plan package pages.
 */
function useDocumentsUrl() {
  const { pathname, search } = useLocation();
  return useCallback(
    (opts: { versionId?: string } & DocumentsParams = {}) => {
      const { versionId, ...params } = opts;
      const path = versionId
        ? pathname
            .split("/")
            .map((s) => (s.startsWith("rpav:") ? versionId : s))
            .join("/")
        : pathname;
      // Merge into the existing search so we don't drop params we don't own, i.e. the `filter` that
      // `usePersistedFilter` keeps in the URL. Dropping it makes the table refetch on row clicks.
      const nextParams = new URLSearchParams(search);
      params.toEntries().forEach(([key, value]) => {
        if (value) {
          nextParams.set(key, value);
        } else {
          nextParams.delete(key);
        }
      });
      const nextSearch = nextParams.toString();
      return nextSearch ? `${path}?${nextSearch}` : path;
    },
    [pathname, search],
  );
}

// If we are modifying ready plan documents, then our changes may create a new draft. If so, then we need to navigate
// to that draft to display the changes we made.
export function useMaybeNavigateToNewDraft() {
  const navigate = useNavigate();
  const documentsUrl = useDocumentsUrl();
  return useCallback(
    (versionId: string | undefined, parents: DocumentsPageDocumentParentFragment[], params: DocumentsParams = {}) => {
      if (versionId) {
        const parent = parents.find((p) => p.__typename === "PlanPackage" && p.aggregateDraft?.id !== versionId);
        if (parent?.__typename === "PlanPackage") {
          pushInNextLoop(navigate, documentsUrl({ versionId: parent.aggregateDraft?.id, ...params }));
          return true;
        }
      }
      return false;
    },
    [navigate, documentsUrl],
  );
}
